import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, KeyRound, ShieldCheck } from 'lucide-react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import TeamCard from './settings/TeamCard';

export default function Settings() {
  const { isOwner } = useAdminAuth();
  // Change password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const handleChangePassword = async () => {
    if (newPassword.length < 12) {
      toast.error('Password must be at least 12 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    setChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success('Password updated. Use it next time you sign in.');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-sm text-gray-400 mt-1">Platform administration and access control</p>
      </div>

      <TeamCard />

      {/* Change Password */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-cyan-400" />
            Change My Password
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-400">
            Update the password for the admin account you're signed in with. Takes effect immediately for your next sign-in.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 max-w-lg">
            <div className="space-y-2">
              <Label className="text-gray-300">New password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="At least 12 characters"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Confirm password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="Re-enter new password"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>
          <Button
            onClick={handleChangePassword}
            disabled={changingPassword || !newPassword || !confirmPassword}
            className="bg-cyan-600 hover:bg-cyan-500 text-white"
          >
            {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
            Update password
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-gray-900/50 border-gray-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            Two-step sign-in
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-gray-400">
          <p>
            On for every portal account. After your password or email code, the portal asks for the 6-digit code from your authenticator app, and asks again every 12 hours.
          </p>
          <p>
            {isOwner
              ? 'If you lose your phone, the authenticator can be removed from the Supabase dashboard, then you set it up again at your next sign-in.'
              : 'If you lose or replace your phone, ask a director to reset your two-step sign-in. You set it up again at your next sign-in.'}
          </p>
        </CardContent>
      </Card>

      {/* Environment Info */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base">Environment</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Supabase Project</span>
              <span className="text-gray-300 font-mono text-xs">Connected</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Admin Portal Version</span>
              <span className="text-gray-300 font-mono text-xs">1.0.0</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
